import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationsPanel from "@/components/NotificationsPanel";
import CommandPalette from "@/components/CommandPalette";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  BookOpen, Target, ListTodo, FolderOpen, CalendarDays, CalendarCheck,
  Users, BarChart3, LogOut, Menu, Search, PanelLeftClose, PanelLeft, Shield, GanttChart, Repeat, Code,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ADMIN_EMAIL = "hrituraj@triomics.com";

const navItems = [
  { to: "/", label: "Goals", icon: Target, end: true },
  { to: "/all-projects", label: "Projects", icon: FolderOpen },
  { to: "/timeline", label: "Timeline", icon: GanttChart },
  { to: "/flat-view", label: "All Tasks", icon: ListTodo },
  { to: "/weekly-roster", label: "Weekly Roster", icon: CalendarDays },
  { to: "/daily-roster", label: "Daily Roster", icon: CalendarCheck },
  { to: "/people", label: "People", icon: Users },
  { to: "/habits", label: "Habits", icon: Repeat },
  { to: "/api", label: "API", icon: Code },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
];

function SidebarContent({ user, onLogout, onNavClick, collapsed }) {
  return (
    <>
      <div>
        <div className={"flex items-center gap-2 mb-5 " + (collapsed ? "justify-center px-0" : "px-2")}>
          <BookOpen className="w-4.5 h-4.5 text-[#37352F] shrink-0" strokeWidth={1.5} />
          {!collapsed && <span className="text-[13px] font-semibold text-[#37352F]">Get Things Done</span>}
        </div>
        <nav className="space-y-px">
          {navItems.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end} onClick={onNavClick}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-2.5 py-[6px] rounded-[4px] transition-colors duration-100 ${collapsed ? "justify-center px-2" : "px-2"} ${
                  isActive
                    ? "bg-[#E0E0DE] text-[#37352F] font-medium"
                    : "text-[#6B6B6B] hover:bg-[#E8E8E6] hover:text-[#37352F]"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="text-[13px]">{item.label}</span>}
            </NavLink>
          ))}
          {user?.email === ADMIN_EMAIL && (
            <NavLink to="/admin" title={collapsed ? "Admin" : undefined}
              className={({ isActive }) => `flex items-center gap-2.5 py-[6px] rounded-[4px] transition-colors duration-100 mt-2 border-t border-[#E0E0DE] pt-2 ${collapsed ? "justify-center px-2" : "px-2"} ${isActive ? "bg-[#E0E0DE] text-[#37352F] font-medium" : "text-[#6B6B6B] hover:bg-[#E8E8E6] hover:text-[#37352F]"}`}
              data-testid="nav-admin">
              <Shield className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="text-[13px]">Admin</span>}
            </NavLink>
          )}
        </nav>
      </div>
      {!collapsed && (
        <div className="border-t border-[#E0E0DE] pt-3 px-2">
          <p className="text-[11px] text-[#6B6B6B] truncate mb-1.5">{user?.name}</p>
          <button onClick={onLogout} data-testid="logout-button"
            className="flex items-center gap-2 text-[12px] text-[#6B6B6B] hover:text-[#37352F] transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center pb-1">
          <button onClick={onLogout} data-testid="logout-button" title="Sign out"
            className="text-[#6B6B6B] hover:text-[#37352F] p-1"><LogOut className="w-4 h-4" /></button>
        </div>
      )}
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex h-screen overflow-hidden bg-white" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col justify-between py-3 px-2 shrink-0 bg-[#FBFBFA] border-r border-[#E8E8E6] transition-all duration-200 ${collapsed ? "w-[52px]" : "w-[220px]"}`} data-testid="sidebar">
        <SidebarContent user={user} onLogout={handleLogout} collapsed={collapsed} />
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-11 flex items-center justify-between px-3 shrink-0 border-b border-[#E8E8E6]" data-testid="topbar">
          <div className="flex items-center gap-1.5">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-1 text-[#6B6B6B] hover:text-[#37352F]" data-testid="mobile-menu-button">
                  <Menu className="w-[18px] h-[18px]" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[220px] bg-[#FBFBFA] p-3 flex flex-col justify-between">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SidebarContent user={user} onLogout={handleLogout} onNavClick={() => setMobileOpen(false)} collapsed={false} />
              </SheetContent>
            </Sheet>
            {/* Collapse toggle */}
            <button onClick={() => setCollapsed(!collapsed)} className="hidden md:flex p-1 text-[#6B6B6B] hover:text-[#37352F] rounded-[4px] hover:bg-[#E8E8E6] transition-colors" data-testid="collapse-sidebar" title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
              {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
            <button onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
              className="hidden sm:flex items-center gap-1.5 px-2 py-1 text-[11px] text-[#6B6B6B] bg-[#F7F6F3] border border-[#E0E0DE] rounded-[4px] hover:bg-[#E0E0DE] transition-colors"
              data-testid="cmd-k-button">
              <Search className="w-3 h-3" /> Search...
              <span className="ml-3 text-[10px] border border-[#B8B6B0] rounded px-1">&#8984;K</span>
            </button>
          </div>
          <NotificationsPanel />
        </header>
        <main className="flex-1 overflow-auto">
          <div className="px-4 md:px-8 py-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
